import React from 'react';
import { Info, CheckCircle2, AlertTriangle, XCircle } from 'lucide-react';

export type AlertVariant = 'info' | 'success' | 'warning' | 'error';

interface AlertProps {
  variant?: AlertVariant;
  title?: string;
  children?: React.ReactNode;
  /** Slot para botões de ação (retry, dismiss). Alinhado à direita. */
  action?: React.ReactNode;
  className?: string;
}

const VARIANT: Record<AlertVariant, { box: string; icon: React.ReactNode; iconColor: string }> = {
  info: {
    box: 'border-blue-200 bg-blue-50 text-blue-900',
    icon: <Info className="h-5 w-5" aria-hidden />,
    iconColor: 'text-blue-600',
  },
  success: {
    box: 'border-emerald-200 bg-emerald-50 text-emerald-900',
    icon: <CheckCircle2 className="h-5 w-5" aria-hidden />,
    iconColor: 'text-emerald-600',
  },
  warning: {
    box: 'border-amber-200 bg-amber-50 text-amber-900',
    icon: <AlertTriangle className="h-5 w-5" aria-hidden />,
    iconColor: 'text-amber-600',
  },
  error: {
    box: 'border-rose-200 bg-rose-50 text-rose-900',
    icon: <XCircle className="h-5 w-5" aria-hidden />,
    iconColor: 'text-rose-600',
  },
};

/**
 * Callout inline padrão. Substitui os `border-red-200 bg-red-50 ...`
 * cru que aparecem em AtivosPage, AtivoNovoPage, DashboardAtivosPage,
 * UsersPage (success), etc.
 *
 * Uso típico:
 *   - erro global de mutação: `<Alert variant="error" title="...">msg</Alert>`
 *   - aviso informativo: `<Alert variant="info">...</Alert>`
 *   - com ação: passar `action={<Button>Tentar novamente</Button>}`
 *
 * Para notificações efêmeras (toast), use outro componente — este é
 * pra callout inline persistente.
 */
export function Alert({ variant = 'info', title, children, action, className = '' }: AlertProps) {
  const cfg = VARIANT[variant];
  return (
    <div
      role={variant === 'error' || variant === 'warning' ? 'alert' : 'status'}
      className={`flex items-start gap-3 rounded-md border p-3 text-sm ${cfg.box} ${className}`.trim()}
    >
      <span className={`mt-0.5 shrink-0 ${cfg.iconColor}`}>{cfg.icon}</span>
      <div className="flex-1 min-w-0">
        {title && <p className="font-medium">{title}</p>}
        {children && <div className={title ? 'mt-1' : ''}>{children}</div>}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  );
}
